import { z } from 'zod';
import { MoneySchema } from './common.js';
// FinancialStatementResponse is the shared wire shape for BS / IS / CF.
//
// Per-row "kind" mirrors the prototype's FinRow union (HEADER section title,
// SUB subtotal/sub-section, LINE leaf line, TOTAL grand total) but flattened
// to a single object so Zod / createZodDto don't have to model a tagged union.
// The frontend maps `kind` -> the existing `type` discriminator used by
// FinTable in apps/web/src/routes/reports.tsx.
//
// `val` / `prev` are signed wire-format numbers. MoneySchema requires gte(0)
// strings, so the union with z.coerce.number() lets us round-trip negatives
// (cash outflows on CF, net losses on IS, etc.) without rejecting them at the
// boundary.
const DecimalLike = z.union([MoneySchema, z.coerce.number()]);
export const FinStatementRowKindSchema = z.enum(['HEADER', 'SUB', 'LINE', 'TOTAL']);
export const FinStatementRowSchema = z.object({
    kind: FinStatementRowKindSchema,
    // null for HEADER / SUB / TOTAL aggregate rows (only LINE rows carry a code).
    code: z.string().nullable(),
    label: z.string(),
    // val: current-period value; signed per AccountType (assets/expenses positive
    // Dr, liab/equity/revenue positive Cr). prev: comparison period (0 when no
    // compare window was provided).
    val: DecimalLike,
    prev: DecimalLike,
    // Visual indent for LINE rows; subtotals/totals always render flush left.
    indent: z.number().int().min(0).max(3).optional(),
});
export const FinancialStatementSchema = z.enum(['BALANCE_SHEET', 'INCOME_STATEMENT', 'CASH_FLOW']);
export const FinancialStatementResponseSchema = z.object({
    statement: FinancialStatementSchema,
    // BS only — the snapshot date. null for IS/CF.
    asOf: z.coerce.date().nullable(),
    // IS/CF only — the period under review. null for BS.
    dateFrom: z.coerce.date().nullable(),
    dateTo: z.coerce.date().nullable(),
    rows: z.array(FinStatementRowSchema),
});
// ===== Query DTO schemas =====
// Service-side date conversion stays consistent with ledger/trial-balance, so
// we accept date strings and coerce to Date in the controller.
const PeriodSchema = z
    .string()
    .regex(/^\d{6}$/)
    .optional();
const DateStrSchema = z.iso.date().optional();
export const BalanceSheetQuerySchema = z.object({
    asOfDate: DateStrSchema,
    compareAsOfDate: DateStrSchema,
    period: PeriodSchema,
});
export const IncomeStatementQuerySchema = z.object({
    dateFrom: DateStrSchema,
    dateTo: DateStrSchema,
    compareFrom: DateStrSchema,
    compareTo: DateStrSchema,
    period: PeriodSchema,
});
export const CashFlowQuerySchema = IncomeStatementQuerySchema;
//# sourceMappingURL=financial-statements.js.map